import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RpcError } from 'grpc-web';
import { RoleEndpointClient } from '../../../../repository/UserManagement/Managers/RoleServiceClientPb';
import { AddRoleRequest, AddRoleResponse, UpdateRoleRequest, UpdateRoleResponse } from '../../../../repository/UserManagement/Managers/role_pb';
import { API_URL, getCurrentLng, getHeaders, ApiMessage, toApiMessage, APIRequest, refreshSession, getSession, ACCOUNTANT_API_URL } from '../../../../app/Api';
import { AccountTypeEndpointClient } from '../../../../repository/Accountant/Account_typeServiceClientPb';
import { formatDate, timestampToDate } from '../../../../app/Helpers';
import { DynamicField } from '../../../common/DynamicFields/DynamicFields';
import { LimitationEndpointClient } from '../../../../repository/Accountant/LimitationServiceClientPb';
import { AddLimitationRequest, AddLimitationResponse, GetLimitationRequest, UpdateLimitationRequest, UpdateLimitationResponse, GetLimitationResponse } from '../../../../repository/Accountant/limitation_pb';

export interface LimitationsFormState {
    isLoading: boolean
    message?: ApiMessage
    stage1: any,
}

const initialState: LimitationsFormState = {
    isLoading: false,
    message: undefined,
    stage1: undefined,
}

const client = new LimitationEndpointClient(ACCOUNTANT_API_URL, null, null);

export const add = createAsyncThunk<AddLimitationResponse.AsObject, APIRequest<AddLimitationRequest>, {

    rejectValue: ApiMessage
}>(
    'forms/accountant/limitations/add',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {
            try {
                var r = await client.add(req.body, req.headers ?? {});
                return r.toObject();
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }
)


export const update = createAsyncThunk<UpdateLimitationResponse.AsObject, APIRequest<UpdateLimitationRequest>, {

    rejectValue: ApiMessage
}>(
    'forms/accountant/limitations/update',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {
            try {
                var r = await client.update(req.body, req.headers ?? {});
                return r.toObject();
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }
)



export const get = createAsyncThunk<GetLimitationResponse.AsObject, APIRequest<GetLimitationRequest>, {

    rejectValue: ApiMessage
}>(
    'forms/accountant/limitations/get',
    async (req, thunkApi) => {
        let callReq = async (): Promise<any> => {
            try {
                var r = await client.getLimitation(req.body, req.headers ?? {});
                return r.toObject();
            } catch (err) {
                try {
                    let message = toApiMessage((err as RpcError).metadata);
                    if (message.data == 401) {
                        var isSessionRefreshed = await refreshSession();
                        if (isSessionRefreshed) {
                            req.headers = {
                                ...req.headers,
                                "Authorization": 'Bearer ' + getSession().accesstoken?.token?.value,
                            }
                            return await callReq();
                        }
                    }
                    return thunkApi.rejectWithValue(message)
                } catch (err) {

                    return thunkApi.rejectWithValue({ body: "genericErrorBody", data: "", title: "genericErrorTitle", type: -2 } as ApiMessage)
                }
            }
        }
        return await callReq();
    }
)

export const limitationsFormSlice = createSlice({
    name: 'forms/accountant/limitations',
    initialState,
    reducers: {

        dismissMessage: (state) => {
            state.message = undefined;
        },
        reset: (state) => {
            state.isLoading = false;
            state.message = undefined;
            state.stage1 = initialState.stage1;
        },
        setStage1: (state, action: PayloadAction<any>) => {
            let stage1 = {
                id: action.payload?.id,
                name: action.payload?.name,
                isAvailable: action.payload?.isAvailable,
                isActive: action.payload?.isActive,
                type: action.payload?.type,
                createdDate: action.payload?.createdDate,
                addedBy: action.payload?.addedBy,
                addedById: action.payload?.addedById,
                extraFields: action.payload?.extraFields,
                balanceMaxLimit: action.payload?.balanceMaxLimit,
                balanceMinLimit: action.payload?.balanceMinLimit,
                transactionLimitations: action.payload?.transactionLimitations,

            }
            state.stage1 = stage1;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(add.fulfilled, (state, { payload }) => {
            state.isLoading = false;
            if (payload)
                state.message = toApiMessage(payload.success?.message);

        })
        builder.addCase(add.rejected, (state, action) => {
            state.isLoading = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(add.pending, (state, action) => {
            state.isLoading = true;
            state.message = undefined;
        })
        builder.addCase(update.fulfilled, (state, { payload }) => {
            if (payload)
                state.message = toApiMessage(payload.success?.message);
            state.isLoading = false;
        })
        builder.addCase(update.rejected, (state, action) => {
            state.isLoading = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(update.pending, (state, action) => {
            state.isLoading = true;
            state.message = undefined;
        })

        builder.addCase(get.fulfilled, (state, { payload }) => {
            state.isLoading = false
            if (payload) {

                let r = {
                    id: payload?.success?.id,
                    name: payload?.success?.name?.value ?? "",
                    isAvailable: payload?.success?.isavailable?.value ?? undefined,
                    isActive: payload?.success?.isactive?.value ?? undefined,
                    type: payload?.success?.type?.value ?? undefined,
                    balanceMaxLimit: payload?.success?.balancemaxlimit?.value ?? undefined,
                    balanceMinLimit: payload?.success?.balanceminlimit?.value ?? undefined,
                    transactionLimitations: [] as any[],
                    createdDate: formatDate(timestampToDate(payload?.success?.createddate?.seconds, payload?.success?.createddate?.nanos)),
                    addedBy: (payload?.success?.createdby?.firstname?.value ?? "") + " " + (payload?.success?.createdby?.lastname?.value ?? ""),
                    addedById: payload?.success?.createdby?.id?.value,
                    extraFields: [] as DynamicField[]
                }
                if (payload?.success?.extrafieldsList) {
                    let count = 0
                    payload?.success?.extrafieldsList?.forEach(element => {
                        r.extraFields.push({ key: element.key, label: element.label, type: element.type, value: element.value })
                        count++
                    });
                }
                if (payload?.success?.transactionlimitationsList) {
                    payload?.success?.transactionlimitationsList?.forEach(element => {
                        //let d: number = element.seconds?.value ?? 0;

                        /*let year = Math.floor(d / 31536000);
                        let month = Math.floor((d % 31536000) / 2628000);
                        let day = Math.floor(((d % 31536000) % 2628000) / 86400);
                        let hour = Math.floor((d % (3600 * 24)) / 3600);
                        let minute = Math.floor((d % 3600) / 60);*/
                        let item = {
                            operation: { key: element.operation?.value ?? 0, text: "" },
                            minAmountValuePerTransaction: element.minamountvaluepertransaction?.value,
                            maxAmountValuePerTransaction: element.maxamountvaluepertransaction?.value,
                            isCreditor: { key: element.iscreditor?.value ? 1 : 2, text: "" },
                            timePeriodLimitations: [] as any[],
                        }
                        element.timeperiodlimitationsList?.forEach(element => {

                            item.timePeriodLimitations.push({
                                period: { key: element.seconds?.value, text: "" },
                                seconds: element.seconds?.value,
                                maxNumberOfTransactions: element.maxnumberoftransactions?.value,
                                maxTransactionsTotalAmount: element.maxtransactionstotalamount?.value
                            })
                        });

                        r.transactionLimitations.push(item);
                    });
                }
                /*if (payload?.success?.collectionaccountsList) {
                    let count = 0
                    payload?.success?.collectionaccountsList?.forEach(element => {
                        r.extraFields.push({ key: element.account?.id, label: element.label, type: element.type, value: element.value })
                        count++
                    });
                }*/

                state.stage1 = r
            }
        })
        builder.addCase(get.rejected, (state, action) => {
            state.isLoading = false;
            if (action.payload) {
                state.message = action.payload;
            }
        })
        builder.addCase(get.pending, (state, action) => {
            state.isLoading = true;
            state.message = undefined;
        })
    }
})

export const { reset, dismissMessage, setStage1 } = limitationsFormSlice.actions

export default limitationsFormSlice.reducer


